/**
 * The column chooser — Step 42.
 *
 * Pick which columns the queue shows, and in what order. Persisted in the URL, and
 * therefore saved with the VIEW like every other piece of queue state — the
 * existing "one representation for the URL, the API and storage" rule, rather
 * than a second home in `saved_views.columns`. So "triage" and "review" can
 * be genuinely different layouts of the same tickets rather than the same
 * seven cells.
 *
 * Order matters as much as membership: a desk that works by group wants Group
 * next to Subject, not at the far right past the SLA pill. So this reorders
 * with explicit up/down rather than drag — the same choice the automation
 * list made, and for the same reason: repeated small moves must not depend on
 * a pointer gesture landing precisely, and up/down works from a keyboard.
 */
import { ArrowDown, ArrowUp, Columns3 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';
import { MANDATORY_COLUMNS, QUEUE_COLUMNS, normaliseColumnIds } from '../../features/tickets/columns';

export function ColumnChooser({
  value,
  onChange,
  onClose,
}: {
  value: string[];
  onChange: (ids: string[]) => void;
  onClose: () => void;
}) {
  // Through the aliases first, so a stored `assignee` ticks `assignment`
  // rather than silently disappearing when this list is saved back.
  const shown = normaliseColumnIds(value).filter((id) => QUEUE_COLUMNS.some((c) => c.id === id));
  const hidden = QUEUE_COLUMNS.filter((c) => !shown.includes(c.id));

  const move = (i: number, dir: -1 | 1) => {
    const next = [...shown];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j]!, next[i]!];
    onChange(next);
  };

  return (
    <div className="fixed inset-x-2 top-28 z-40 max-h-[65vh] overflow-y-auto rounded-md border bg-card p-2 shadow-lg sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-1 sm:max-h-none sm:w-72">
      <div className="flex items-center gap-2 px-1 pb-2">
        <Columns3 className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs font-semibold">Columns</span>
        <div className="flex-1" />
        <button
          onClick={onClose}
          className="text-xs text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          Done
        </button>
      </div>

      <p className="px-1 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        Shown
      </p>
      {shown.map((id, i) => {
        const col = QUEUE_COLUMNS.find((c) => c.id === id)!;
        const locked = MANDATORY_COLUMNS.has(id);
        return (
          <div key={id} className="flex items-center gap-1 rounded px-1 py-0.5 hover:bg-accent/60">
            <span className="min-w-0 flex-1 truncate text-xs">{col.label}</span>
            <Button size="sm" variant="ghost" aria-label={`Move ${col.label} up`} onClick={() => move(i, -1)}>
              <ArrowUp />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              aria-label={`Move ${col.label} down`}
              onClick={() => move(i, 1)}
            >
              <ArrowDown />
            </Button>
            <button
              disabled={locked}
              onClick={() => onChange(shown.filter((x) => x !== id))}
              // Subject cannot be removed: without it a row is a set of codes
              // with nothing identifying the ticket. Disabled and explained
              // rather than absent, so the rule is visible.
              title={locked ? 'The subject is always shown' : undefined}
              className={cn(
                'shrink-0 px-1 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                locked ? 'cursor-not-allowed text-muted-foreground/40' : 'text-brand hover:underline',
              )}
            >
              Hide
            </button>
          </div>
        );
      })}

      {hidden.length > 0 && (
        <>
          <p className="px-1 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Available
          </p>
          {hidden.map((col) => (
            <div key={col.id} className="flex items-center gap-1 rounded px-1 py-0.5 hover:bg-accent/60">
              <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">{col.label}</span>
              <button
                onClick={() => onChange([...shown, col.id])}
                className="shrink-0 px-1 text-xs text-brand hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                Show
              </button>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
